import React from "react";
import { reportFeatureFailure } from "@/lib/report-problem";

type Props = {
  /** Short name of the area, used in the report only — never shown raw. */
  feature: string;
  /** Optional friendlier label used in the fallback copy. */
  label?: string;
  /** Render nothing instead of the fallback card (for small decorative areas). */
  silent?: boolean;
  children: React.ReactNode;
};

type State = { hasError: boolean; key: number };

/**
 * Wraps one feature area so a failure inside it can never blank the page.
 * The rest of the page keeps working; this area shows a gentle message and a
 * Try Again button that re-mounts only the failed part.
 *
 * Nothing the member wrote is ever included in the report — only the error
 * name, message and stack.
 */
export class FeatureBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, key: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    const err = error instanceof Error ? error : new Error(String(error));
    reportFeatureFailure({
      source: "client_error",
      title: `${this.props.feature}: ${err.name}: ${err.message}`.slice(0, 200),
      detail: [err.stack, info.componentStack].filter(Boolean).join("\n\n").slice(0, 4000),
      feature: this.props.feature,
      severity: "error",
      context: { boundary: this.props.feature },
    });
  }

  private retry = () => this.setState((s) => ({ hasError: false, key: s.key + 1 }));

  render() {
    if (!this.state.hasError) {
      return <React.Fragment key={this.state.key}>{this.props.children}</React.Fragment>;
    }
    if (this.props.silent) return null;

    return (
      <div
        role="status"
        className="my-4 rounded-2xl border border-border bg-card p-5 text-center"
      >
        <p className="text-sm text-foreground">
          {this.props.label
            ? `${this.props.label} didn't load correctly.`
            : "Something didn't load correctly."}{" "}
          Please try again.
        </p>
        <button
          type="button"
          onClick={this.retry}
          className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground"
        >
          Try again
        </button>
      </div>
    );
  }
}

/** Convenience wrapper for small global widgets: fail quietly, never break the page. */
export function SilentBoundary({
  feature,
  children,
}: {
  feature: string;
  children: React.ReactNode;
}) {
  return (
    <FeatureBoundary feature={feature} silent>
      {children}
    </FeatureBoundary>
  );
}
